import { CONNECTION_STATUS_AUTO_OPEN_DURATION_MS } from 'Modules/softphone/components/SoftphoneConnectionStatus/index.constants';
import type { SoftphoneConnectionState } from 'Modules/softphone/components/SoftphoneConnectionStatus/index.types';
import { useConnectionAnalytics } from 'Modules/softphone/hooks/useConnectionAnalytics';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { useEffect, useRef, useState } from 'react';

/**
 * Controls the auto-open behavior of the poor-connection tooltip.
 */
export function usePoorConnectionTooltip(
  status: SoftphoneConnectionState
): boolean {
  const [isAutoOpen, setIsAutoOpen] = useState(false);

  const isConnectionLossFeedbackActive = useSoftphoneStore(
    (state) => state.isConnectionLossFeedbackActive
  );

  const { trackPoorConnectionTooltipShown } = useConnectionAnalytics();
  const trackRef = useRef(trackPoorConnectionTooltipShown);
  trackRef.current = trackPoorConnectionTooltipShown;

  useEffect(() => {
    const { hasPoorTooltipShown, setHasPoorTooltipShown } =
      useSoftphoneStore.getState();

    if (
      status !== 'poor' ||
      isConnectionLossFeedbackActive ||
      hasPoorTooltipShown
    ) {
      return;
    }

    setIsAutoOpen(true);
    setHasPoorTooltipShown(true);
    trackRef.current();

    const timer = setTimeout(
      () => setIsAutoOpen(false),
      CONNECTION_STATUS_AUTO_OPEN_DURATION_MS
    );

    return () => {
      clearTimeout(timer);
      setIsAutoOpen(false);
    };
  }, [status, isConnectionLossFeedbackActive]);

  return isAutoOpen;
}
